<template>
  <div class="loading-page" :style="{ height }">
    <div class="progress-wrapper">
      <el-progress type="circle" :percentage="progress" />
    </div>
  </div>
</template>
<script>
import { mapMutations } from 'vuex'
import getname from '../../api/Getname'
import getgroup from '../../api/Group'
import getuserName from '../../api/User'
import { VTKLoader } from 'three/examples/jsm/loaders/VTKLoader.js'
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js'
import { directionPointerLoad } from '../ModelRendering/Pointer'
import * as THREE from 'three'
import {
  computeBoundsTree,
  disposeBoundsTree,
  acceleratedRaycast,
} from 'three-mesh-bvh'

THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree
THREE.Mesh.prototype.raycast = acceleratedRaycast

export default {
  data() {
    return {
      height: document.documentElement.clientHeight + 'px',
      progress: 0,
      listColor: [],
      modelIdMap: [],
      vtkItems: [],
      plyItems: [],
      params: null,
      parentObject: new THREE.Object3D(),
    }
  },
  methods: {
    ...mapMutations([
      'setBufferGeometry', 'setListColor', 'setMeshList',
      'setUserInfo', 'setBoundingBox', 'setMaterials',
      'setGroupInfo', 'setPeopleModel',
    ]),

    updateProgress(done, total) {
      this.progress = Math.round((done / total) * 100)
    },

    async fetchModelMeta() {
      const res = await getname.Getnames(this.params.modelNum)
      this.listColor = res.data
      this.listColor.forEach(item => {
        item.icons = Number(item.modelOpacity) !== 0
        if (item.modelBrandFlag == 1) {
          this.vtkItems.push(item)
        } else {
          this.plyItems.push(item)
        }
      })
    },

    async fetchGroupInfo() {
      try {
        const res = await getgroup.getgroups(this.params.modelNum)
        this.setGroupInfo(res.data)
      } catch (e) {
        console.error('Failed to fetch group info:', e)
      }
    },

    loadVtkModels() {
      const loader = new VTKLoader()
      const total = this.vtkItems.length + this.plyItems.length

      return this.vtkItems.map(item => {
        this.modelIdMap.push({ id: item.modelId, name: item.modelName })
        return new Promise((resolve) => {
          loader.load(`/api/api${item.modelAddress}`, (geometry) => {
            this.progress = Math.round((++this._completedVtk / total) * 100)
            resolve(geometry)
          })
        })
      })
    },

    loadPlyModels() {
      const loader = new PLYLoader()
      const total = this.vtkItems.length + this.plyItems.length
      const objects = []

      if (this.plyItems.length === 0) return Promise.resolve([])

      const requests = this.plyItems.map(item => {
        return new Promise((resolve) => {
          loader.load(`/api/api${item.modelAddress}`, (geometry) => {
            const material = new THREE.MeshPhongMaterial({
              vertexColors: true,
              side: THREE.DoubleSide,
              shininess: 10,
              specular: 0x111111,
            })
            const mesh = new THREE.Mesh(geometry, material)
            mesh.model_id = item.modelId
            mesh.name = item.modelName
            try {
              geometry.computeBoundsTree()
              geometry.computeVertexNormals()
              objects.push(mesh)
              this.parentObject.add(mesh)
            } catch (e) {
              console.warn('PLY geometry skipped:', item.modelName, e.message)
            }
            this.progress = Math.round((++this._completedPly / total) * 100)
            resolve(mesh)
          })
        })
      })

      return Promise.all(requests).then(() => objects)
    },

    createColoredMeshes(geometries) {
      const objects = []
      this.vtkItems.forEach((item, index) => {
        if (!item.modelColor || !geometries[index]) return

        const opacity = Number(item.modelOpacity)
        const material = new THREE.MeshPhongMaterial({
          color: `rgb(${item.modelColor})`,
          side: THREE.DoubleSide,
          shininess: 10,
          specular: 0x111111,
          flatShading: false,
        })

        if (opacity < 1) {
          material.transparent = true
          material.opacity = opacity
          material.depthWrite = false
        }

        if (!geometries[index].attributes.position) return
        try {
          geometries[index].computeVertexNormals()
          geometries[index].computeBoundsTree()
        } catch (e) {
          console.warn('VTK geometry skipped:', this.modelIdMap[index]?.name, e.message)
          return
        }
        const mesh = new THREE.Mesh(geometries[index], material)
        mesh.model_id = this.modelIdMap[index].id
        mesh.name = this.modelIdMap[index].name
        mesh.castShadow = true
        objects.push(mesh)
        this.parentObject.add(mesh)
      })
      return objects
    },

    computeBoundingBoxes() {
      const boxes = []
      this.parentObject.traverse((child) => {
        if (child instanceof THREE.Mesh && child.geometry.attributes.position) {
          child.geometry.computeBoundingBox()
          const box = child.geometry.boundingBox.clone()
          box.applyMatrix4(child.matrixWorld)
          boxes.push({ mesh: child, boundingBox: box })
        }
      })
      return boxes
    },

    async fetchUserInfo() {
      if (this.$store.state.userInfo) return
      try {
        const res = await getuserName.Getnames(this.params.modelNum)
        this.setUserInfo(res.data)
      } catch (e) {
        console.error('Failed to fetch user info:', e)
      }
    },

    async loadAllModels() {
      this._completedVtk = 0
      this._completedPly = 0

      await this.fetchModelMeta()
      this.fetchUserInfo()
      const peopleModel = await directionPointerLoad()
      this.setPeopleModel(peopleModel)
      this.setListColor(this.listColor)
      localStorage.setItem('listColor', JSON.stringify(this.listColor))
      await this.fetchGroupInfo()

      const [vtkGeometries, plyObjects] = await Promise.all([
        Promise.all(this.loadVtkModels()),
        this.loadPlyModels(),
      ])

      const coloredMeshes = this.createColoredMeshes(vtkGeometries)
      const allObjects = [...coloredMeshes, ...plyObjects]

      this.setMeshList(allObjects)
      this.setMaterials(this.parentObject)

      const boxes = this.computeBoundingBoxes()
      this.setBoundingBox(boxes)

      this.progress = 99
      setTimeout(() => {
        this.$router.push(`/index?userId=${this.params.modelNum}&modelNum=${this.params.modelNum}`)
      }, 500)
    },
  },

  created() {
    const ur = window.location.hash
    const params = new URLSearchParams(ur)
    const p = {}
    params.forEach((v, k) => { p[k] = v })
    this.params = p
  },

  mounted() {
    const textEl = document.getElementsByClassName('el-progress__text')[0]
    if (textEl) textEl.style.color = 'black'
    this.loadAllModels()
  },
}
</script>
<style scoped lang="scss">
.loading-page {
  background: gray;
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;

  .progress-wrapper {
    // centering handled by flex parent
  }
}
</style>
